import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Banner, Button, Card, compareMoney, Dialog, Field, formatMoney, isZero, Money, PageHeader, Pill, Tabs,
} from '@elysian/ui';
import type { Account, BankAccountSummary, BankLine, MatchSuggestion, ReconcileAction, TaxCode } from '../../data/source';
import { useAsync } from '../../data/useAsync';
import { useSession } from '../../state/session';
import { useToast } from '../../state/toast';
import { iso, longDate, shortDate } from '../invoices/shared';
import { Pager, ShowSize, SortTh, useClientList } from '../shared/ListControls';

type Filter = 'unmatched' | 'matched' | 'excluded' | 'all';

export function actionFor(s: MatchSuggestion): ReconcileAction | null {
  if (s.kind === 'documents' && s.document_ids?.length) return { action: 'documents', document_ids: s.document_ids };
  if (s.kind === 'journal_lines' && s.journal_line_ids?.length) return { action: 'match', journal_line_ids: s.journal_line_ids };
  if (s.kind === 'rule' && s.rule_id) return { action: 'create', rule_id: s.rule_id };
  return null;
}

interface Loaded { accounts: BankAccountSummary[]; ledgerAccounts: Account[]; codes: TaxCode[] }

export function Bank() {
  const { source, today } = useSession();
  const [version, setVersion] = useState(0);
  const data = useAsync<Loaded>(async () => {
    const [accounts, ledgerAccounts, codes] = await Promise.all([source.bankAccounts(), source.accounts(), source.taxCodes(iso(today()))]);
    return { accounts, ledgerAccounts, codes };
  }, [source, version], { keep: true });
  if (data.state === 'loading') return <PageHeader title="Reconcile" subtitle="Loading…" />;
  if (data.state === 'error') return <><PageHeader title="Reconcile" /><Banner>Couldn’t load bank accounts: {data.error.message}</Banner></>;
  if (!data.data.accounts.length) return <><PageHeader title="Reconcile" /><Banner tone="info">Add a bank account in the chart of accounts first.</Banner></>;
  return <Reconcile {...data.data} refreshAccounts={() => setVersion((v) => v + 1)} />;
}

function Reconcile({ accounts, ledgerAccounts, codes, refreshAccounts }: Loaded & { refreshAccounts: () => void }) {
  const { source } = useSession();
  const toast = useToast((s) => s.show);
  const [params, setParams] = useSearchParams();
  const code = params.get('account') && accounts.some((a) => a.code === params.get('account')) ? params.get('account')! : accounts[0]!.code;
  const account = accounts.find((a) => a.code === code)!;
  const [filter, setFilter] = useState<Filter>('unmatched');
  const [version, setVersion] = useState(0);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [dealing, setDealing] = useState<BankLine | null>(null);
  const [uploading, setUploading] = useState(false);

  const data = useAsync(async () => {
    const lines = await source.bankLines(code);
    const asAt = lines.map((l) => l.txn_date).sort().at(-1) ?? iso(new Date());
    const status = await source.reconciliation(code, asAt);
    return { lines, status, asAt };
  }, [source, code, version], { keep: true });
  const reload = () => { setVersion((v) => v + 1); refreshAccounts(); };

  async function apply(line: BankLine, action: ReconcileAction, done: string) {
    setBusy(line.id);
    setError(null);
    try {
      await source.reconcile(line.id, action);
      toast(done);
      setDealing(null);
      reload();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  }

  async function unmatch(line: BankLine) {
    setBusy(line.id);
    setError(null);
    try {
      await source.unmatch(line.id);
      toast('Line unmatched');
      reload();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  }

  async function signOff(asAt: string) {
    setBusy('signoff');
    setError(null);
    try {
      await source.signOff(code, asAt);
      toast(`${account.name} signed off at ${longDate(asAt)}`);
      reload();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  }

  const lines = data.state === 'ready' ? data.data.lines : [];
  const matching = lines.filter((l) => filter === 'all' || l.status === filter);
  // #49, #50: 50, 100 or all, and sorting on the headings, over every line of the account.
  const list = useClientList(matching, {
    key: 'elysian.bank-lines', initial: { sort: 'date', dir: 'desc' }, filterKey: `${code}|${filter}`,
    sorts: { date: (l) => l.txn_date, description: (l) => l.description.toLowerCase(), amount: (l) => Number(l.amount) },
  });
  const shown = list.rows;
  const count = (f: Filter) => (f === 'all' ? lines.length : lines.filter((l) => l.status === f).length);
  const st = data.state === 'ready' ? data.data.status : null;
  const balanced = !!st && st.unreconciled === 0 && isZero(st.difference);
  const signedOff = !!st?.signed_off_at;

  return (
    <>
      <PageHeader
        title="Reconcile"
        subtitle={account.statement_date ? `${account.name} · statement to ${longDate(account.statement_date)}` : `${account.name} · no statement imported yet`}
        actions={
          <>
            {accounts.length > 1 && (
              <select className="el-input" aria-label="Bank account" style={{ width: 'auto' }} value={code}
                onChange={(e) => { setParams({ account: e.target.value }); setFilter('unmatched'); }}>
                {accounts.map((a) => <option key={a.code} value={a.code}>{a.name}</option>)}
              </select>
            )}
            <Button onClick={() => setUploading(true)}>Import statement</Button>
          </>
        }
      />
      {error && <div className="el-error-box" role="alert" style={{ marginBottom: 16 }}>{error}</div>}

      {st && data.state === 'ready' && (
        <div className="el-grid" data-min="220">
          <Card className="el-stat"><div className="el-stat-label">Bank statement</div><div className="el-stat-value"><Money value={st.statement_balance} /></div><div className="el-stat-note">at {longDate(st.as_at)}</div></Card>
          <Card className="el-stat"><div className="el-stat-label">In the books</div><div className="el-stat-value"><Money value={st.ledger_balance} /></div><div className="el-stat-note">same date</div></Card>
          <Card className="el-stat">
            <div className="el-stat-label">Difference</div>
            <div className="el-stat-value" style={!isZero(st.difference) ? { color: 'var(--el-warn-icon)' } : undefined}><Money value={st.difference} /></div>
            <div className="el-stat-note">{st.unreconciled ? `${st.unreconciled} ${st.unreconciled === 1 ? 'line' : 'lines'} to deal with` : 'Every line dealt with'}</div>
          </Card>
          <Card className="el-stat">
            <div className="el-stat-label">Sign-off</div>
            {signedOff
              ? <><div className="el-stat-value" style={{ fontSize: 20 }}>Signed off</div><div className="el-stat-note">{longDate(st.as_at)}</div></>
              : (
                <div style={{ marginTop: 8 }}>
                  <Button variant="primary" disabled={!balanced || busy === 'signoff'} onClick={() => void signOff(data.data.asAt)}>Sign off {shortDate(data.data.asAt)}</Button>
                  <div className="el-stat-note">{balanced ? 'Statement and books agree' : `Last signed off ${account.last_signed_off ? shortDate(account.last_signed_off) : 'never'}`}</div>
                </div>
              )}
          </Card>
        </div>
      )}

      <Card>
        <Tabs<Filter> label="Show lines" value={filter} onChange={setFilter} tabs={[
          { key: 'unmatched', label: 'To deal with', count: count('unmatched') },
          { key: 'matched', label: 'Matched', count: count('matched') },
          { key: 'excluded', label: 'Excluded', count: count('excluded') },
          { key: 'all', label: 'All', count: count('all') },
        ]} />
        <div className="el-row" style={{ justifyContent: 'flex-end', padding: '10px 16px 0' }}><ShowSize value={list.size} onChange={list.setSize} /></div>
        <div className="el-table-wrap">
          <table className="el-table" aria-label="Bank lines" style={{ minWidth: 720 }}>
            <thead><tr>
              <SortTh label="Date" col="date" state={list.sortBy} onSort={list.setSortBy} firstDir="desc" />
              <SortTh label="Description" col="description" state={list.sortBy} onSort={list.setSortBy} />
              <SortTh label="Amount" col="amount" state={list.sortBy} onSort={list.setSortBy} numeric />
              <th scope="col">Match</th>
            </tr></thead>
            <tbody>
              {data.state === 'loading' && <tr><td colSpan={4}><div className="el-skeleton" style={{ height: 18 }} aria-label="Loading bank lines" /></td></tr>}
              {data.state === 'ready' && shown.length === 0 && (
                <tr><td colSpan={4} className="el-muted">{filter === 'unmatched' ? 'Nothing to deal with. Import the next statement when it arrives.' : 'No lines here.'}</td></tr>
              )}
              {shown.map((l) => {
                const s = l.best_suggestion ?? null;
                const act = s ? actionFor(s) : null;
                return (
                  <tr key={l.id}>
                    <td style={{ whiteSpace: 'nowrap' }}>{shortDate(l.txn_date)}</td>
                    <td className="el-bank-desc">{l.description}</td>
                    <td className="el-num"><Money value={l.amount} signed tone={compareMoney(l.amount, '0') > 0 ? 'positive' : undefined} /></td>
                    <td>
                      {l.status === 'unmatched' && (
                        <div className="el-match">
                          {s && act && (
                            <>
                              <span className="el-match-label">{s.label}<span className="el-conf" data-level={s.score >= 0.9 ? 'high' : s.score >= 0.6 ? 'mid' : 'low'}>{Math.round(s.score * 100)}%</span></span>
                              <Button size="sm" variant="primary" disabled={busy === l.id} onClick={() => void apply(l, act, `Matched: ${s.label}`)}>Match</Button>
                            </>
                          )}
                          <Button size="sm" disabled={busy === l.id} onClick={() => { setError(null); setDealing(l); }}>{s ? 'Something else' : 'Deal with it'}</Button>
                        </div>
                      )}
                      {l.status === 'matched' && (
                        <div className="el-match"><Pill tone="ok">Matched</Pill><Button size="sm" variant="ghost" disabled={busy === l.id} onClick={() => void unmatch(l)}>Unmatch</Button></div>
                      )}
                      {l.status === 'excluded' && (
                        <div className="el-match"><Pill tone="draft">Excluded</Pill><span className="el-hint">{l.excluded_reason}</span><Button size="sm" variant="ghost" disabled={busy === l.id} onClick={() => void unmatch(l)}>Undo</Button></div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {data.state === 'ready' && <Pager count={list.count} page={list.page} size={list.size} shown={shown.length} onPage={list.setPage} />}
      </Card>
      <p className="el-hint" style={{ marginTop: 12 }}>Tick “Always do this” when you post a line, and lines like it are suggested for you next time.</p>

      {dealing && (
        <DealWith line={dealing} bankCode={code} ledgerAccounts={ledgerAccounts} codes={codes}
          onClose={() => setDealing(null)} onApply={(a, msg) => apply(dealing, a, msg)} busy={busy === dealing.id} error={error} />
      )}
      {uploading && <Upload code={code} accountName={account.name} onClose={() => setUploading(false)} onDone={(msg) => { setUploading(false); toast(msg); reload(); }} />}
    </>
  );
}

type Mode = 'suggestions' | 'customer' | 'supplier' | 'create' | 'transfer' | 'exclude';

function DealWith({ line, bankCode, ledgerAccounts, codes, onClose, onApply, busy, error }: {
  line: BankLine; bankCode: string; ledgerAccounts: Account[]; codes: TaxCode[]; busy: boolean; error: string | null;
  onClose: () => void; onApply: (a: ReconcileAction, done: string) => Promise<void>;
}) {
  const { source } = useSession();
  const incoming = compareMoney(line.amount, '0') > 0;
  const suggestions = useAsync(() => source.bankSuggestions(line.id), [source, line.id]);
  const [mode, setMode] = useState<Mode>('create');
  const usable = ledgerAccounts.filter((a) => a.is_active && a.control === 'none' && a.class === (incoming ? 'income' : 'expense'));
  const others = ledgerAccounts.filter((a) => a.control === 'bank' && a.code !== bankCode);
  const [account, setAccount] = useState('');
  const [tax, setTax] = useState('');
  const [description, setDescription] = useState(line.description);
  const [remember, setRemember] = useState(false);
  const [matchText, setMatchText] = useState(line.description.split(/\s+\d/)[0] ?? line.description);
  const [other, setOther] = useState(others[0]?.code ?? '');
  const [reason, setReason] = useState('');
  const chosen = usable.find((a) => a.code === account);

  async function create() {
    if (remember) {
      await source.createBankRule({ name: matchText.trim(), match_text: matchText.trim(), direction: incoming ? 'in' : 'out', account_code: account, tax_code: tax || chosen?.default_tax_code || undefined });
    }
    await onApply({ action: 'create', account_code: account, tax_code: tax || chosen?.default_tax_code || undefined, description: description.trim() }, `Posted to ${account} ${chosen?.name ?? ''}`.trim());
  }

  const list = suggestions.state === 'ready' ? suggestions.data : [];
  return (
    <Dialog title={`${incoming ? 'Money in' : 'Money out'}: ${formatMoney(incoming ? line.amount : line.amount.replace('-', ''))}`} onClose={onClose}
      actions={<Button variant="ghost" onClick={onClose}>Cancel</Button>}>
      <p className="el-hint" style={{ margin: 0 }}>{shortDate(line.txn_date)} · {line.description}</p>
      <div className="el-segmented" role="tablist" aria-label="What is it?">
        {([...(list.length ? ['suggestions'] : []), incoming ? 'customer' : 'supplier', 'create', 'transfer', 'exclude'] as Mode[]).map((m) => (
          <button key={m} type="button" role="tab" aria-selected={mode === m} onClick={() => setMode(m)}>
            {m === 'suggestions' ? 'Suggestions' : m === 'customer' ? 'From a customer' : m === 'supplier' ? 'To a supplier' : m === 'create' ? (incoming ? 'Other income' : 'An expense') : m === 'transfer' ? 'Transfer' : 'Exclude'}
          </button>
        ))}
      </div>

      {mode === 'suggestions' && (
        <div className="el-stack" style={{ gap: 8 }}>
          {list.map((s, i) => {
            const act = actionFor(s);
            return act && (
              <div key={i} className="el-spread el-suggestion">
                <span>{s.label}<span className="el-conf" data-level={s.score >= 0.9 ? 'high' : s.score >= 0.6 ? 'mid' : 'low'}>{Math.round(s.score * 100)}%</span></span>
                <Button size="sm" variant="primary" disabled={busy} onClick={() => void onApply(act, `Matched: ${s.label}`)}>Match</Button>
              </div>
            );
          })}
        </div>
      )}

      {mode === 'customer' && <FromContact role="customer" line={line} busy={busy} onApply={onApply} />}
      {mode === 'supplier' && <FromContact role="supplier" line={line} busy={busy} onApply={onApply} />}

      {mode === 'create' && (
        <>
          <Field id="deal-account" label="Account">
            <select id="deal-account" className="el-input" value={account} onChange={(e) => { setAccount(e.target.value); setTax(''); }}>
              <option value="">Choose…</option>
              {usable.map((a) => <option key={a.code} value={a.code}>{a.code} {a.name}</option>)}
            </select>
          </Field>
          <Field id="deal-vat" label="VAT">
            <select id="deal-vat" className="el-input" value={tax || chosen?.default_tax_code || ''} onChange={(e) => setTax(e.target.value)}>
              <option value="">No VAT</option>
              {codes.map((c) => <option key={c.code} value={c.code}>{c.name}</option>)}
            </select>
          </Field>
          <Field id="deal-desc" label="Description">
            <input id="deal-desc" className="el-input" value={description} onChange={(e) => setDescription(e.target.value)} />
          </Field>
          <label className="el-check"><input type="checkbox" checked={remember} onChange={(e) => setRemember(e.target.checked)} /> Always do this for lines containing</label>
          {remember && <input className="el-input" aria-label="Text to look for" value={matchText} onChange={(e) => setMatchText(e.target.value)} />}
          <div className="el-dialog-actions"><Button variant="primary" disabled={!account || busy || (remember && !matchText.trim())} onClick={() => void create()}>Post it</Button></div>
        </>
      )}

      {mode === 'transfer' && (
        others.length ? (
          <>
            <Field id="deal-other" label={incoming ? 'From' : 'To'}>
              <select id="deal-other" className="el-input" value={other} onChange={(e) => setOther(e.target.value)}>
                {others.map((a) => <option key={a.code} value={a.code}>{a.code} {a.name}</option>)}
              </select>
            </Field>
            <div className="el-dialog-actions"><Button variant="primary" disabled={!other || busy} onClick={() => void onApply({ action: 'transfer', other_account_code: other }, 'Transfer recorded')}>Record transfer</Button></div>
          </>
        ) : <p className="el-hint" style={{ margin: 0 }}>There’s no other bank account to transfer to.</p>
      )}

      {mode === 'exclude' && (
        <>
          <p className="el-hint" style={{ margin: 0 }}>For a line that shouldn’t be in the books, such as a duplicate from an overlapping download. It no longer counts towards the statement balance.</p>
          <Field id="deal-reason" label="Reason">
            <input id="deal-reason" className="el-input" value={reason} onChange={(e) => setReason(e.target.value)} />
          </Field>
          <div className="el-dialog-actions"><Button variant="danger" disabled={reason.trim().length < 3 || busy} onClick={() => void onApply({ action: 'exclude', reason: reason.trim() }, 'Line excluded')}>Exclude line</Button></div>
        </>
      )}
      {error && <div className="el-error-box" role="alert">{error}</div>}
    </Dialog>
  );
}

function Upload({ code, accountName, onClose, onDone }: { code: string; accountName: string; onClose: () => void; onDone: (msg: string) => void }) {
  const { source } = useSession();
  const [file, setFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  async function go() {
    if (!file) return;
    setBusy(true);
    setError(null);
    try {
      const r = await source.uploadStatement(code, file);
      onDone(`${r.imported} ${r.imported === 1 ? 'line' : 'lines'} imported${r.duplicates ? `, ${r.duplicates} already there` : ''}`);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setBusy(false);
    }
  }
  return (
    <Dialog title={`Import a statement into ${accountName}`} onClose={onClose}
      actions={<><Button variant="ghost" onClick={onClose}>Cancel</Button><Button variant="primary" disabled={!file || busy} onClick={() => void go()}>Import</Button></>}>
      <p className="el-hint" style={{ margin: 0 }}>CSV from AIB, Bank of Ireland, PTSB or Revolut, OFX, or CAMT.053 XML. Lines already imported are skipped, so overlapping downloads are fine.</p>
      <Field id="stmt-file" label="Statement file">
        <input id="stmt-file" type="file" className="el-input" style={{ paddingTop: 10 }} accept=".csv,.ofx,.qfx,.xml,text/csv,application/xml"
          onChange={(e) => setFile(e.target.files?.[0] ?? null)} />
      </Field>
      {error && <div className="el-error-box" role="alert">{error}</div>}
    </Dialog>
  );
}

/**
 * Money in from a customer, or out to a supplier (#26): tick the invoices (bills) it pays,
 * oldest first; anything over stays on their account as credit (no fee account needed).
 */
function FromContact({ role, line, busy, onApply }: { role: 'customer' | 'supplier'; line: BankLine; busy: boolean; onApply: (a: ReconcileAction, done: string) => Promise<void> }) {
  const { source } = useSession();
  const sales = role === 'customer';
  const people = useAsync(() => source.contacts(role), [source, role]);
  const [who, setWho] = useState('');
  const docs = useAsync(async () => ({ who, list: who ? (await source.documents({ type: sales ? 'sales_invoice' : 'purchase_bill', contactId: who }))
    .filter((d) => (d.status === 'posted' || d.status === 'part_paid') && Number(d.outstanding) > 0 && d.currency === 'EUR')
    .sort((a, b) => a.issue_date.localeCompare(b.issue_date)) : [] }), [source, who, sales]);
  const [ticked, setTicked] = useState<Set<string> | null>(null);
  const [tickedFor, setTickedFor] = useState('');
  const loaded = docs.state === 'ready' && docs.data.who === who;
  const list = loaded ? docs.data.list : [];
  const amount = Math.abs(Math.round(Number(line.amount) * 100));
  // Once this contact's documents have loaded (not the last one's): tick oldest first.
  if (loaded && who && tickedFor !== who) {
    setTickedFor(who);
    // Oldest first while the money lasts.
    let left = amount;
    const start = new Set<string>();
    for (const d of list) { if (left <= 0) break; start.add(d.id); left -= Math.round(Number(d.outstanding) * 100); }
    setTicked(start);
  }
  let left = amount;
  const paying = list.filter((d) => ticked?.has(d.id)).map((d) => { const take = Math.max(0, Math.min(left, Math.round(Number(d.outstanding) * 100))); left -= take; return take; });
  const used = paying.reduce((t, x) => t + x, 0);
  const extra = amount - used;
  const name = people.state === 'ready' ? people.data.find((c) => c.id === who)?.name : undefined;
  const what = sales ? 'invoices' : 'bills';

  return (
    <>
      <Field id={`deal-${role}`} label={sales ? 'Customer' : 'Supplier'}>
        <select id={`deal-${role}`} className="el-input" value={who} onChange={(e) => { setWho(e.target.value); setTicked(null); }}>
          <option value="">Choose…</option>
          {(people.state === 'ready' ? people.data : []).map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
      </Field>
      {who && loaded && (list.length === 0
        ? <p className="el-hint" style={{ margin: 0 }}>No unpaid {what}: it all goes on their account as credit.</p>
        : <div className="el-stack" style={{ gap: 4 }}>
          {list.map((d) => (
            <label key={d.id} className="el-check">
              <input type="checkbox" checked={ticked?.has(d.id) ?? false}
                onChange={(e) => setTicked((t) => { const n = new Set(t); if (e.target.checked) n.add(d.id); else n.delete(d.id); return n; })} />
              {d.doc_no}{!sales && d.supplier_ref ? ` (${d.supplier_ref})` : ''} · {shortDate(d.issue_date)} · {formatMoney(d.outstanding)} unpaid
            </label>
          ))}
        </div>)}
      {who && (
        <p className="el-hint" style={{ margin: 0 }} aria-live="polite">
          {formatMoney((used / 100).toFixed(2))} against {what}{extra > 0 ? `, ${formatMoney((extra / 100).toFixed(2))} on account as credit` : ''}.
        </p>
      )}
      <div className="el-dialog-actions">
        <Button variant="primary" disabled={!who || busy} onClick={() => void onApply(
          { action: 'on_account', contact_id: who, document_ids: list.filter((d) => ticked?.has(d.id)).map((d) => d.id) },
          `${sales ? 'Receipt from' : 'Payment to'} ${name ?? (sales ? 'the customer' : 'the supplier')} recorded${extra > 0 ? `, ${formatMoney((extra / 100).toFixed(2))} on account` : ''}`)}>
          {sales ? 'Record receipt' : 'Record payment'}
        </Button>
      </div>
    </>
  );
}
